import axios, { type AxiosRequestConfig } from "axios";

import axiosInstance from "@lib/axios";

import {
  formatRecentActivityApiDateParam,
  formatRecentActivityExportFileName,
  type RecentActivityRange,
} from "./recent-activity-utils";

export const RECENT_ACTIVITY_EXPORT_POLL_INTERVAL_MS = 2000;
export const RECENT_ACTIVITY_EXPORT_TIMEOUT_MS = Number.POSITIVE_INFINITY;

const RECENT_ACTIVITY_EXPORT_FAILURE_STATUSES = new Set([
  "failed",
  "failure",
  "error",
  "cancelled",
  "canceled",
]);

export type RecentActivityExportFormat = "csv" | "parquet";

export type RecentActivityExportJobResponse = {
  task_id: string;
  status: string;
  row_count: number;
  estimated_size_bytes: number;
  download_url: string | null;
  detail: string | null;
};

export type RecentActivityExportHttpGet = (
  url: string,
  config?: AxiosRequestConfig
) => Promise<{
  status: number;
  data: unknown;
}>;

type CreateRecentActivityExportJobArgs = {
  storeId: string;
  startDate: Date;
  endDate: Date;
  eventTypes?: string[];
  isReturningCustomer?: boolean;
  ordering?: string[];
  format?: RecentActivityExportFormat;
  apiBaseUrl?: string;
  httpGet?: RecentActivityExportHttpGet;
};

type GetRecentActivityExportJobStatusArgs = {
  storeId: string;
  taskId: string;
  apiBaseUrl?: string;
  httpGet?: RecentActivityExportHttpGet;
};

type DownloadRecentActivityExportFileArgs = {
  downloadUrl: string;
  filename: string;
  apiBaseUrl?: string;
  httpGet?: RecentActivityExportHttpGet;
  triggerBrowserDownload?: typeof triggerDownload;
  createObjectUrl?: (blob: Blob) => string;
  revokeObjectUrl?: (objectUrl: string) => void;
};

const getApiBaseUrl = (apiBaseUrl?: string) => {
  const resolvedApiBaseUrl =
    apiBaseUrl ?? process.env.NEXT_PUBLIC_TRACIFY_API_BASE_URL;

  if (!resolvedApiBaseUrl) {
    throw new Error("Missing NEXT_PUBLIC_TRACIFY_API_BASE_URL");
  }

  return resolvedApiBaseUrl;
};

const cleanEventTypes = (eventTypes: string[] | undefined) =>
  Array.from(
    new Set(
      (eventTypes ?? [])
        .map((eventType) => eventType.trim())
        .filter((eventType) => eventType.length > 0)
    )
  );

const getErrorMessage = (error: unknown, fallback: string) => {
  if (!axios.isAxiosError(error)) {
    return error instanceof Error && error.message ? error.message : fallback;
  }

  const responseData = error.response?.data as
    | { error?: string; detail?: string }
    | undefined;

  return responseData?.error ?? responseData?.detail ?? error.message;
};

export function waitForMs(ms: number) {
  return new Promise<void>((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

export function triggerDownload({
  downloadUrl,
  filename,
}: {
  downloadUrl: string;
  filename: string;
}) {
  const link = document.createElement("a");
  link.href = downloadUrl;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
}

export function buildRecentActivityExportJobUrl({
  storeId,
  startDate,
  endDate,
  eventTypes,
  isReturningCustomer,
  ordering = ["-timestamp"],
  format = "csv",
  apiBaseUrl,
}: Omit<CreateRecentActivityExportJobArgs, "httpGet">) {
  const resolvedApiBaseUrl = getApiBaseUrl(apiBaseUrl);
  const params = new URLSearchParams({
    start_date: formatRecentActivityApiDateParam(startDate),
    end_date: formatRecentActivityApiDateParam(endDate),
    format,
  });

  if (isReturningCustomer !== undefined) {
    params.append("is_returning_customer", String(isReturningCustomer));
  }

  cleanEventTypes(eventTypes).forEach((eventType) => {
    params.append("event_types", eventType);
  });

  ordering.forEach((order) => {
    params.append("ordering", order);
  });

  return `${resolvedApiBaseUrl}/analytics/events/${storeId}/export/async?${params.toString()}`;
}

export async function createRecentActivityExportJob({
  httpGet = axiosInstance.get.bind(axiosInstance),
  ...args
}: CreateRecentActivityExportJobArgs): Promise<RecentActivityExportJobResponse> {
  try {
    const response = await httpGet(buildRecentActivityExportJobUrl(args), {
      headers: {
        Accept: "application/json",
      },
    });

    if (response.status !== 200) {
      throw new Error("Failed to create recent activity export job");
    }

    return response.data as RecentActivityExportJobResponse;
  } catch (error) {
    throw new Error(
      getErrorMessage(
        error,
        "Failed to create recent activity export job. Please try again."
      )
    );
  }
}

export async function getRecentActivityExportJobStatus({
  storeId,
  taskId,
  apiBaseUrl,
  httpGet = axiosInstance.get.bind(axiosInstance),
}: GetRecentActivityExportJobStatusArgs): Promise<RecentActivityExportJobResponse> {
  const resolvedApiBaseUrl = getApiBaseUrl(apiBaseUrl);

  try {
    const response = await httpGet(
      `${resolvedApiBaseUrl}/analytics/events/${storeId}/export/async/${taskId}`,
      {
        headers: {
          Accept: "application/json",
        },
      }
    );

    if (response.status !== 200) {
      throw new Error("Failed to fetch recent activity export job");
    }

    return response.data as RecentActivityExportJobResponse;
  } catch (error) {
    throw new Error(
      getErrorMessage(
        error,
        "Failed to fetch recent activity export job. Please try again."
      )
    );
  }
}

export function isFailedRecentActivityExportStatus(
  status: string | null | undefined
) {
  if (!status) {
    return false;
  }

  return RECENT_ACTIVITY_EXPORT_FAILURE_STATUSES.has(
    status.trim().toLowerCase()
  );
}

export async function waitForRecentActivityExportDownloadUrl({
  initialJob,
  storeId,
  apiBaseUrl,
  getExportJobStatus = getRecentActivityExportJobStatus,
  pollIntervalMs = RECENT_ACTIVITY_EXPORT_POLL_INTERVAL_MS,
  timeoutMs = RECENT_ACTIVITY_EXPORT_TIMEOUT_MS,
  wait = waitForMs,
  now = Date.now,
  onPollingStart,
}: {
  initialJob: RecentActivityExportJobResponse;
  storeId: string;
  apiBaseUrl?: string;
  getExportJobStatus?: (
    args: GetRecentActivityExportJobStatusArgs
  ) => Promise<RecentActivityExportJobResponse>;
  pollIntervalMs?: number;
  timeoutMs?: number;
  wait?: (ms: number) => Promise<void>;
  now?: () => number;
  onPollingStart?: () => void;
}) {
  if (initialJob.download_url?.trim()) {
    return initialJob.download_url;
  }

  if (isFailedRecentActivityExportStatus(initialJob.status)) {
    throw new Error(
      initialJob.detail?.trim() ||
        "Failed to export recent activity. Please try again."
    );
  }

  if (!initialJob.task_id) {
    throw new Error(
      "Failed to start recent activity export. Please try again."
    );
  }

  onPollingStart?.();

  let exportJob = initialJob;
  const startedAt = now();

  while (now() - startedAt < timeoutMs) {
    await wait(pollIntervalMs);

    exportJob = await getExportJobStatus({
      storeId,
      taskId: exportJob.task_id,
      apiBaseUrl,
    });

    if (exportJob.download_url?.trim()) {
      return exportJob.download_url;
    }

    if (isFailedRecentActivityExportStatus(exportJob.status)) {
      throw new Error(
        exportJob.detail?.trim() ||
          "Failed to export recent activity. Please try again."
      );
    }
  }

  throw new Error("Export is still processing. Please try again.");
}

export async function downloadRecentActivityExportFile({
  downloadUrl,
  filename,
  apiBaseUrl,
  httpGet = axiosInstance.get.bind(axiosInstance),
  triggerBrowserDownload = triggerDownload,
  createObjectUrl = (blob: Blob) => window.URL.createObjectURL(blob),
  revokeObjectUrl = (objectUrl: string) =>
    window.URL.revokeObjectURL(objectUrl),
}: DownloadRecentActivityExportFileArgs) {
  const resolvedApiBaseUrl = getApiBaseUrl(apiBaseUrl);
  const resolvedDownloadUrl = new URL(
    downloadUrl,
    resolvedApiBaseUrl
  ).toString();
  const apiOrigin = new URL(resolvedApiBaseUrl).origin;

  if (new URL(resolvedDownloadUrl).origin !== apiOrigin) {
    triggerBrowserDownload({
      downloadUrl: resolvedDownloadUrl,
      filename,
    });
    return;
  }

  const response = await httpGet(resolvedDownloadUrl, {
    responseType: "blob",
  });
  const blob =
    response.data instanceof Blob
      ? response.data
      : new Blob([response.data as BlobPart]);
  const objectUrl = createObjectUrl(blob);

  try {
    triggerBrowserDownload({
      downloadUrl: objectUrl,
      filename,
    });
  } finally {
    revokeObjectUrl(objectUrl);
  }
}

export async function exportRecentActivityCsv({
  storeId,
  startDate,
  endDate,
  eventTypes,
  isReturningCustomer,
  apiBaseUrl,
  createExportJob = createRecentActivityExportJob,
  getExportJobStatus = getRecentActivityExportJobStatus,
  downloadFile = downloadRecentActivityExportFile,
  pollIntervalMs = RECENT_ACTIVITY_EXPORT_POLL_INTERVAL_MS,
  timeoutMs = RECENT_ACTIVITY_EXPORT_TIMEOUT_MS,
  wait = waitForMs,
  now = Date.now,
  onPollingStart,
}: {
  storeId: string;
  startDate: Date;
  endDate: Date;
  eventTypes?: string[];
  isReturningCustomer?: boolean;
  apiBaseUrl?: string;
  createExportJob?: typeof createRecentActivityExportJob;
  getExportJobStatus?: (
    args: GetRecentActivityExportJobStatusArgs
  ) => Promise<RecentActivityExportJobResponse>;
  downloadFile?: (args: DownloadRecentActivityExportFileArgs) => Promise<void>;
  pollIntervalMs?: number;
  timeoutMs?: number;
  wait?: (ms: number) => Promise<void>;
  now?: () => number;
  onPollingStart?: () => void;
}) {
  const initialJob = await createExportJob({
    storeId,
    startDate,
    endDate,
    eventTypes,
    isReturningCustomer,
    ordering: ["-timestamp"],
    format: "csv",
    apiBaseUrl,
  });

  const downloadUrl = await waitForRecentActivityExportDownloadUrl({
    initialJob,
    storeId,
    apiBaseUrl,
    getExportJobStatus,
    pollIntervalMs,
    timeoutMs,
    wait,
    now,
    onPollingStart,
  });

  await downloadFile({
    downloadUrl,
    filename: formatRecentActivityExportFileName({
      startDate,
      endDate,
    } satisfies RecentActivityRange),
    apiBaseUrl,
  });
}
